import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon, CheckIcon, ChevronRightIcon, MapPinIcon, SmartphoneIcon, BoxIcon } from "lucide-react";
import { topicPrefs } from "../../data/avis";
import { useAuth } from "../../contexts/AuthContext";
import { useDebug } from "../../contexts/DebugContext";
type Step = 'courriel' | 'identite' | 'categories' | 'confirme' | 'deja-inscrit' | 'adresse' | 'telephone';
interface NoticeSignupFormProps {
  /** Appelé après « Voir mes alertes », une fois la session ouverte. */
  onDone?: () => void;
  /** Appelé sur « Continuer sans compte ». */
  onSkip?: () => void;
  /** Remonte le titre de l'étape courante au conteneur (popup ou page). */
  onTitleChange?: (title: string) => void;
}
const stepTitles: Record<Step, string> = {
  courriel: 'Recevoir les alertes citoyennes',
  identite: 'Vos informations',
  categories: 'Les sujets qui vous intéressent',
  confirme: 'Vos alertes sont activées',
  'deja-inscrit': 'Recevoir les alertes citoyennes',
  adresse: 'Votre adresse',
  telephone: 'Votre numéro de téléphone'
};
const fieldBase = 'mt-1.5 w-full rounded-xl border px-4 py-3 text-[15px] text-ville-navy transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo/20';
const fieldClass = `${fieldBase} border-slate-200 focus:border-ville-indigo`;

/** Bordure rouge tant que le champ obligatoire est vide. */
const fieldWithState = (invalid: boolean) => invalid ? `${fieldBase} border-red-500 focus:border-red-500` : fieldClass;
const labelClass = 'block text-[13px] font-bold text-ville-navy';
const primaryButton = 'inline-flex items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:hover:translate-y-0';
const backButton = 'inline-flex items-center gap-2 text-sm font-bold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
interface ExtraRowProps {
  icon: BoxIcon;
  label: string;
  hint: string;
  value: string;
  onClick: () => void;
}

/** Ligne « Ajouter une adresse / un téléphone », cochée une fois remplie. */
function ExtraRow({
  icon: Icon,
  label,
  hint,
  value,
  onClick
}: ExtraRowProps) {
  const filled = value !== '';
  return <button type="button" onClick={onClick} className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-150 ease-ville hover:bg-ville-sand focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-495" data-mp="38xn39fwfwsb4">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ville-deep" aria-hidden="true" data-id="element-496" data-mp="3hvpcuju8ota0" />
      <span className="min-w-0 flex-1" data-id="element-497" data-mp="3dxrotkc7gd87">
        <span className="block text-sm font-semibold text-ville-navy" data-id="element-498" data-mp="f376k66zd0l">{label}</span>
        <span className="block truncate text-[13px] text-slate-500" data-id="element-499" data-mp="da2vbj1x4vr">{filled ? value : hint}</span>
      </span>
      {filled ? <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-label="Rempli" data-id="element-500" data-mp="3nk2lqjnmk8ny" /> : <ChevronRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-501" data-mp="3sivopi5ach9v" />}
    </button>;
}

/** Catégories cochées d'office à l'inscription. */
const defaultTopics = ['collectes', 'loisirs', 'municipal'];

/** Adresses fictives de Beaconsfield proposées en autocomplétion. */
const addressSuggestions = ['366, chemin Lakeshore, Beaconsfield', '288, boulevard Beaconsfield, Beaconsfield', '303, rue Sherbrooke, Beaconsfield', '130, Taywood Drive, Beaconsfield', '15, City Lane, Beaconsfield', '340, rue Preston, Beaconsfield', '538, chemin Beaurepaire, Beaconsfield', '100, Harwood Gate, Beaconsfield', '50, avenue Willowbrook, Beaconsfield'];

/** Met le numéro au format québécois 000 000-0000. */
function formatQuebecPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Prénom du compte retrouvé à partir du courriel saisi. */
function accountFirstName(email: string): string {
  const local = email.trim().split('@')[0] ?? '';
  const first = local.split(/[._-]/)[0] ?? '';
  if (!first) return '';
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/** Inscription aux alertes : courriel, puis identité et catégories si le compte est nouveau. */
export function NoticeSignupForm({
  onDone,
  onSkip,
  onTitleChange
}: NoticeSignupFormProps) {
  const {
    login
  } = useAuth();
  const {
    alreadySubscribed,
    sso
  } = useDebug();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('courriel');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [topics, setTopics] = useState<string[]>(defaultTopics);
  const [emailTouched, setEmailTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [addressDraft, setAddressDraft] = useState('');
  const [addressOpen, setAddressOpen] = useState(false);
  const [phoneModes, setPhoneModes] = useState<Array<'sms' | 'appel'>>(['sms']);
  const [phoneDraft, setPhoneDraft] = useState('');
  useEffect(() => {
    onTitleChange?.(stepTitles[step]);
  }, [step, onTitleChange]);
  const query = addressDraft.trim().toLowerCase();
  const addressMatches = query === '' ? addressSuggestions.slice(0, 5) : addressSuggestions.filter(item => item.toLowerCase().includes(query)).slice(0, 5);
  const toggleTopic = (id: string) => setTopics(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  const openNotifications = () => {
    login(email.trim());
    onDone?.();
    window.open('/notifications?espace=1', '_blank', 'noopener,noreferrer');
  };
  return <div data-id="element-502" data-mp="rjfki2iq33o5">
      {step === 'courriel' && <>
          <p className="text-[15px] leading-relaxed text-slate-600" data-id="element-503" data-mp="mm73w6d837p0">
            Recevez par courriel les alertes qui vous intéressent.
          </p>
          <form className="mt-8" noValidate onSubmit={event => {
        event.preventDefault();
        if (!/\S+@\S+\.\S+/.test(email)) {
          setEmailTouched(true);
          return;
        }
        setEmailTouched(false);
        // Le compte est vérifié automatiquement à partir du courriel.
        setStep(alreadySubscribed ? 'deja-inscrit' : 'identite');
      }} data-id="element-504" data-mp="3v05ustlc82vn">
            <p data-id="element-505" data-mp="3c9xzgxnvqgq0">
              <label htmlFor="avis-courriel" className={labelClass} data-id="element-506" data-mp="3c9ob1eihghmf">
                Courriel
              </label>
              <input id="avis-courriel" type="email" value={email} onChange={event => setEmail(event.target.value)} autoComplete="email" aria-invalid={emailTouched} className={fieldWithState(emailTouched)} data-id="element-507" data-mp="1muncl0t0c00d" />
            </p>

            <div className="mt-5 flex justify-end" data-id="element-508" data-mp="1duqg4d0dlwur">
              <button type="submit" className={primaryButton} data-id="element-509" data-mp="1drujcs09le7w">
                Continuer
              </button>
            </div>
          </form>
        </>}

      {step === 'identite' && <>
          <form className="mt-6" noValidate onSubmit={event => {
        event.preventDefault();
        if (firstName.trim() === '' || lastName.trim() === '') {
          setNameTouched(true);
          return;
        }
        setNameTouched(false);
        setStep('categories');
      }} data-id="element-510" data-mp="26je44gcif0c0">
            <div className="grid gap-4 sm:grid-cols-2" data-id="element-511" data-mp="21houn54xquq1">
              <p data-id="element-512" data-mp="l50bqvojq9bu">
                <label htmlFor="avis-prenom" className={labelClass} data-id="element-513" data-mp="l4i6j9jwokrs">
                  Prénom
                </label>
                <input id="avis-prenom" value={firstName} onChange={event => setFirstName(event.target.value)} autoComplete="given-name" aria-invalid={nameTouched && firstName.trim() === ''} className={fieldWithState(nameTouched && firstName.trim() === '')} data-id="element-514" data-mp="q5o17cx4mi3y" />
              </p>
              <p data-id="element-515" data-mp="17ut8aqh24do7">
                <label htmlFor="avis-nom" className={labelClass} data-id="element-516" data-mp="17u9vfo69kfh1">
                  Nom
                </label>
                <input id="avis-nom" value={lastName} onChange={event => setLastName(event.target.value)} autoComplete="family-name" aria-invalid={nameTouched && lastName.trim() === ''} className={fieldWithState(nameTouched && lastName.trim() === '')} data-id="element-517" data-mp="12wrna002m4a3" />
              </p>
            </div>

            <div className="mt-5 flex items-center justify-between gap-4" data-id="element-518" data-mp="1qqt0o2dy5j8b">
              <button type="button" onClick={() => {
            setNameTouched(false);
            setStep('courriel');
          }} className={backButton} data-id="element-519" data-mp="1lshnbhog0qlc">
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-520" data-mp="2ei6pvzgswyp2" />
                Retour
              </button>
              <button type="submit" className={primaryButton} data-id="element-521" data-mp="2mrch4do7dngg">
                Continuer
              </button>
            </div>
          </form>
        </>}

      {step === 'categories' && <>
          <form className="mt-6" onSubmit={event => {
        event.preventDefault();
        setStep('confirme');
      }} data-id="element-522" data-mp="2vqzmtbnytffb">
            <fieldset data-id="element-523" data-mp="30rnelzk0q0al">
              <legend className="sr-only" data-id="element-524" data-mp="30rx2nhli5xdn">Catégories d’alertes</legend>
              <div className="mb-2 flex items-start gap-3 rounded-2xl border border-ville-indigo bg-ville-indigo-soft px-4 py-3" data-id="element-525" data-mp="35onjw891mz0n">
                <input type="checkbox" checked readOnly aria-readonly="true" aria-label="Urgences — catégorie obligatoire" onClick={event => event.preventDefault()} onKeyDown={event => event.preventDefault()} className="mt-0.5 h-4 w-4 cursor-default accent-ville-navy" data-id="element-526" data-mp="39ph8l3ub8hul" />
                <span className="min-w-0 flex-1" data-id="element-527" data-mp="3oh9d0kskgbhj">
                  <span className="flex items-center gap-2" data-id="element-528" data-mp="3tfaresf1rmmo">
                    <span className="font-bold text-ville-navy" data-id="element-529" data-mp="3tfsvgb7yb555">Urgences</span>
                    <span className="rounded-full bg-ville-navy px-2.5 py-0.5 text-[11px] font-bold text-white" data-id="element-530" data-mp="3ffyrvbgv1m2o">
                      Toujours activé
                    </span>
                  </span>
                  <span className="block text-[13px] text-slate-600" data-id="element-531" data-mp="3oekiltwhk7ej">
                    Avis d’ébullition, évacuations et sécurité publique
                  </span>
                </span>
              </div>
              <ul className="space-y-2" data-id="element-532" data-mp="1z9akrxh54kf">
                {topicPrefs.map(topic => {
              const checked = topics.includes(topic.id);
              return <li key={topic.id} data-id="element-533" data-mp="b0pjswcjz0n2">
                      <label className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition-colors duration-150 ease-ville ${checked ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200'}`} data-id="element-534" data-mp="b1sbv7lm394k">
                        <input type="checkbox" checked={checked} onChange={() => toggleTopic(topic.id)} className="mt-0.5 h-4 w-4 accent-ville-navy" data-id="element-535" data-mp="2ctx1udjjvwcn" />
                        <span className="min-w-0" data-id="element-536" data-mp="2a7xmyedaizia">
                          <span className="block font-bold text-ville-navy" data-id="element-537" data-mp="2a9qudvvq9nrc">{topic.label}</span>
                          <span className="block text-[13px] text-slate-600" data-id="element-538" data-mp="25euqnyh4pbye">{topic.description}</span>
                        </span>
                      </label>
                    </li>;
            })}
              </ul>
            </fieldset>

            <div className="mt-5 flex items-center justify-between gap-4" data-id="element-539" data-mp="2t71tnghd0h6s">
              <button type="button" onClick={() => setStep('identite')} className={backButton} data-id="element-540" data-mp="2t45slja7tce4">
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-541" data-mp="2o5majtyqskr1" />
                Retour
              </button>
              <button type="submit" disabled={topics.length === 0} className={primaryButton} data-id="element-542" data-mp="2f70rzyt9t5q9">
                Activer mes alertes
              </button>
            </div>
          </form>
        </>}

      {step === 'confirme' && <>
          <p className="text-[15px] text-slate-600" data-id="element-543" data-mp="13qiyo7kyzj7u">
            Vous recevrez les alertes sélectionnées par courriel.
            <br data-id="element-544" data-mp="159ald7vyo0iv" />
            Vous pouvez{' '}
            <button type="button" onClick={() => {
          login(email.trim());
          onDone?.();
          window.open('/notifications/parametres?espace=1', '_blank', 'noopener,noreferrer');
        }} className="font-bold text-ville-indigo underline underline-offset-2 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-545" data-mp="1a7kdf9me2l09">
              modifier vos choix
            </button>{' '}
            ou retirer votre consentement à tout moment.
          </p>

          <div className="mt-5 rounded-2xl border border-slate-200 p-4" data-id="element-546" data-mp="cklq3phl6pf1">
            <p className="text-sm font-bold text-ville-navy" data-id="element-547" data-mp="ci8yslpxv2pb">Personnalisez davantage vos alertes</p>
            <ul className="mt-3 space-y-2" data-id="element-548" data-mp="hgtxggpjlebn">
              <li data-id="element-549" data-mp="3kgh5awqviyr">
                <ExtraRow icon={MapPinIcon} label="Ajouter une adresse" hint="Pour recevoir les alertes de votre secteur" value={address} onClick={() => {
              setAddressDraft(address);
              setStep('adresse');
            }} data-id="element-550" data-mp="3jx9cn0t6ayg" />
              </li>
              <li data-id="element-551" data-mp="3g3jbaifhb0jx">
                <ExtraRow icon={SmartphoneIcon} label="Ajouter un numéro de téléphone" hint="Pour recevoir vos alertes par SMS ou téléphone" value={phone} onClick={() => {
              setPhoneDraft(phone);
              setStep('telephone');
            }} data-id="element-552" data-mp="3g4b4jmllsm71" />
              </li>
            </ul>
          </div>

          <div className="mt-5 flex items-center justify-between gap-4" data-id="element-553" data-mp="7tzryrad46uj">
            <button type="button" onClick={() => setStep('categories')} className={backButton} data-id="element-554" data-mp="2x9co66dtfa6">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-555" data-mp="go4g32y4346d" />
              Retour
            </button>
            <button type="button" onClick={openNotifications} className={primaryButton} data-id="element-556" data-mp="cnc5a59lcr7z">
              Voir mes alertes
            </button>
          </div>
        </>}

      {step === 'adresse' && <form className="mt-6" onSubmit={event => {
      event.preventDefault();
      setAddress(addressDraft.trim());
      setStep('confirme');
    }} data-id="element-557" data-mp="lnqtbcdxwkf6">
          <div className="relative" data-id="element-558" data-mp="15n95blk443v2">
            <label htmlFor="avis-adresse" className={labelClass} data-id="element-559" data-mp="19pvpr1egemi8">
              Numéro, rue et municipalité
            </label>
            <input id="avis-adresse" value={addressDraft} onChange={event => {
          setAddressDraft(event.target.value);
          setAddressOpen(true);
        }} onFocus={() => setAddressOpen(true)} onKeyDown={event => {
          if (event.key === 'Escape') setAddressOpen(false);
        }} autoComplete="off" role="combobox" aria-expanded={addressOpen && addressMatches.length > 0} aria-controls="avis-adresse-liste" aria-autocomplete="list" className={fieldClass} data-id="element-560" data-mp="1ehxcxid5txn1" />
            {addressOpen && addressMatches.length > 0 && <ul id="avis-adresse-liste" role="listbox" aria-label="Adresses proposées" className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg shadow-ville-navy/10" data-id="element-561" data-mp="2c6yww5dmekly">
                {addressMatches.map(suggestion => <li key={suggestion} role="option" aria-selected={addressDraft === suggestion} data-id="element-562" data-mp="3hw7qesf3v3bh">
                    <button type="button" onClick={() => {
              setAddressDraft(suggestion);
              setAddressOpen(false);
            }} className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-[15px] text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-sand focus:bg-ville-sand focus:outline-none" data-id="element-563" data-mp="3dum3ee8ux8nk">
                      <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-564" data-mp="x9cw06xf12c7" />
                      {suggestion}
                    </button>
                  </li>)}
              </ul>}
          </div>
          <p className="mt-2 text-[13px] text-slate-500" data-id="element-565" data-mp="2hq3zob52wld6">
            Nous ciblons les alertes de votre secteur : travaux, collectes et avis de quartier.
          </p>
          <div className="mt-5 flex items-center justify-between gap-4" data-id="element-566" data-mp="2mpghuil0949f">
            <button type="button" onClick={() => setStep('confirme')} className={backButton} data-id="element-567" data-mp="3ag2rgeanqwlj">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-568" data-mp="3fct750ijvq6f" />
              Retour
            </button>
            <button type="submit" disabled={addressDraft.trim() === ''} className={primaryButton} data-id="element-569" data-mp="31dbfmddvdytd">
              Enregistrer
            </button>
          </div>
        </form>}

      {step === 'telephone' && <form className="mt-6" onSubmit={event => {
      event.preventDefault();
      setPhone(phoneDraft.trim());
      setStep('confirme');
    }} data-id="element-570" data-mp="1py4quo6u9o34">
          <p data-id="element-571" data-mp="1mgqjy2s028tf">
            <label htmlFor="avis-telephone" className={labelClass} data-id="element-572" data-mp="1mgh1r1d755ya">
              Numéro de téléphone
            </label>
            <input id="avis-telephone" type="tel" inputMode="tel" value={phoneDraft} onChange={event => setPhoneDraft(formatQuebecPhone(event.target.value))} autoComplete="tel" className={fieldClass} data-id="element-573" data-mp="1hh46bs89eqk3" />
          </p>
          <fieldset className="mt-4" data-id="element-574" data-mp="1rgms8e7qcmes">
            <legend className="text-[13px] font-bold text-ville-navy" data-id="element-575" data-mp="13qip07ygeoob">Recevoir les alertes par</legend>
            <ul className="mt-2 grid gap-2 sm:grid-cols-2" data-id="element-576" data-mp="yro4n1ku8s23">
              {[{
            id: 'sms' as const,
            label: 'SMS',
            hint: 'Message texte'
          }, {
            id: 'appel' as const,
            label: 'Appels',
            hint: 'Appel vocal automatisé'
          }].map(mode => {
            const active = phoneModes.includes(mode.id);
            return <li key={mode.id} data-id="element-577" data-mp="g16d30bqs2ib">
                    <label className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition-colors duration-150 ease-ville ${active ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200'}`} data-id="element-578" data-mp="g054r3m41u4a">
                      <input type="checkbox" checked={active} onChange={() => setPhoneModes(current => current.includes(mode.id) ? current.filter(item => item !== mode.id) : [...current, mode.id])} className="mt-0.5 h-4 w-4 accent-ville-navy" data-id="element-579" data-mp="3vo6ph8y487z3" />
                      <span className="min-w-0" data-id="element-580" data-mp="dh94wpfncw1z">
                        <span className="block text-sm font-bold text-ville-navy" data-id="element-581" data-mp="ded6z13su7dj">{mode.label}</span>
                        <span className="block text-[13px] text-slate-600" data-id="element-582" data-mp="idhezoezlz2a">{mode.hint}</span>
                      </span>
                    </label>
                  </li>;
          })}
            </ul>
          </fieldset>
          <div className="mt-5 flex items-center justify-between gap-4" data-id="element-583" data-mp="w6yzbbfhzftd">
            <button type="button" onClick={() => setStep('confirme')} className={backButton} data-id="element-584" data-mp="w8s3mk2n1ny7">
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-585" data-mp="116bi9l3902qo" />
              Retour
            </button>
            <button type="submit" disabled={phoneDraft.trim() === '' || phoneModes.length === 0} className={primaryButton} data-id="element-586" data-mp="2llzw7kpbrpdb">
              Enregistrer
            </button>
          </div>
        </form>}

      {step === 'deja-inscrit' && <>
          <p className="text-[17px] font-bold text-ville-navy" data-id="element-587" data-mp="27ouwe2g7fmqi">
            Bonjour {accountFirstName(email)}, vous êtes déjà inscrite aux alertes citoyennes.
          </p>
          <p className="mt-1 text-[15px] text-slate-600" data-id="element-588" data-mp="30cz04if7tpqk">
            Vos alertes sont envoyées à {email.trim()}. Vous pouvez les consulter et ajuster vos catégories en
            tout temps.
          </p>
          <button type="button" onClick={openNotifications} className={`mt-5 w-full ${primaryButton}`} data-id="element-589" data-mp="2qnfh9cri5vvp">
            Voir mes alertes
          </button>
        </>}

      {step === 'courriel' && onSkip && sso && <p className="mt-6 border-t border-slate-200 pt-5 text-center" data-id="element-590" data-mp="3jc4kbhc4cba3">
          <button type="button" onClick={() => {
        onSkip();
        // Accès en visiteur : la liste s'ouvre sans passer par l'inscription.
        navigate('/notifications', {
          state: {
            sansCompte: true
          }
        });
      }} className="text-xs font-semibold text-slate-500 underline underline-offset-4 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-591" data-mp="3j9rxapri7aq6">
            Voir les alertes sans m’inscrire
          </button>
        </p>}
    </div>;
}